/**
 * Capture — функции захвата текущего URL/UTM/clickIDs и формирования LogPayload.
 */

import { v7 as uuidv7 } from 'uuid';
import type { ClickIds, LogPayload, UtmParams } from './types';

const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'] as const;

const CLICK_ID_KEYS: (keyof ClickIds)[] = [
  'gclid', 'gbraid', 'wbraid', 'dclid', 'gad_source',
  'yclid', 'ymclid', 'yadclid',
  'fbclid', 'msclkid',
  'ttclid', 'twclid', 'li_fat_id', 'rdt_cid', 'sccid', 'epik',
  'vk_click_id', 'rb_clickid', 'mt_click_id',
  'mc_eid', 'mc_cid',
  '_openstat', 'etext', 'from',
  'awc', 'irclickid', 'obclid', 'tblci',
  'sznclid', 'qclid', 'spclid',
];

/** Извлекает UTM из URL searchParams. */
export function captureUtm(url: string): UtmParams {
  const utm: UtmParams = {};
  if (!url) return utm;

  try {
    const params = new URL(url).searchParams;
    for (const key of UTM_KEYS) {
      const value = params.get(`utm_${key}`);
      if (value) {
        utm[key] = value.toLowerCase().trim().slice(0, 255);
      }
    }
  } catch {
    // невалидный URL
  }

  return utm;
}

/** Извлекает все click IDs из URL searchParams. */
export function captureClickIds(url: string): ClickIds {
  const clickIds: ClickIds = {};
  if (!url) return clickIds;

  try {
    const params = new URL(url).searchParams;
    for (const key of CLICK_ID_KEYS) {
      const value = params.get(key as string);
      if (value) {
        // sccid case-sensitive
        clickIds[key] = (key === 'sccid' ? value : value.toLowerCase()) as never;
      }
    }
  } catch {
    // невалидный URL
  }

  return clickIds;
}

/** Формирует payload для отправки на /api/log/v. */
export function buildPayload(input: {
  evt: 'pageview' | 'event';
  evtName?: string;
  props?: Record<string, unknown>;
  value?: number;
  currency?: string;
  fingerprint?: string;
}): LogPayload {
  const now = Date.now();
  const eid = uuidv7();

  const url = typeof window !== 'undefined' ? window.location.href : '';
  const ref = typeof document !== 'undefined' ? document.referrer : '';
  const title = typeof document !== 'undefined' ? document.title : '';

  const vp: [number, number] | undefined =
    typeof window !== 'undefined'
      ? [window.innerWidth, window.innerHeight]
      : undefined;

  const sc: [number, number] | undefined =
    typeof screen !== 'undefined' ? [screen.width, screen.height] : undefined;

  const lang = typeof navigator !== 'undefined' ? navigator.language : undefined;
  const tz =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : undefined;

  return {
    url,
    ref,
    evt: input.evt,
    evtName: input.evtName,
    props: input.props,
    value: input.value,
    currency: input.currency,
    vp,
    sc,
    lang,
    tz,
    fp: input.fingerprint,
    eid,
    ts: now,
    title,
  };
}
